/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';

import appleTouchIcon from '../images/favicons/apple-touch-icon.png';
import thirtytwo from '../images/favicons/favicon-32x32.png';
import sixteen from '../images/favicons/favicon-16x16.png';
import favicon from '../images/favicons/favicon.ico';

// eslint-disable-next-line react/prop-types
const SEO = ({ description, lang, meta, keywords, title, url, image }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;
  const pageTitle = title || site.siteMetadata.title;

  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    const consent = getCookieConsentValue('cookieConsent');
    if (consent === 'true') {
      setHasConsent(true);
    }
  }, []);

  const handleAccept = () => {
    setHasConsent(true);
  };

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={pageTitle}
        script={
          hasConsent
            ? [
                {
                  src: 'https://www.googletagmanager.com/gtag/js?id=AW-17796662025',
                  async: true,
                },
                {
                  innerHTML: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', 'AW-17796662025');
                  `,
                },
              ]
            : []
        }
        link={[
          {
            rel: 'apple-touch-icon',
            sizes: '180x180',
            href: `${appleTouchIcon}`,
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '32x32',
            href: `${thirtytwo}`,
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '16x16',
            href: `${sixteen}`,
          },
          { rel: 'shortcut icon', href: `${favicon}` },
        ]}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: pageTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: 'og:url',
            content: url,
          },
          {
            property: 'og:image',
            content: image,
          },
          {
            property: 'og:image:width',
            content: '1366',
          },
          {
            property: 'og:image:height',
            content: '768',
          },
          {
            property: 'og:image:alt',
            content: pageTitle,
          },
          {
            property: 'og:site_name',
            content: 'Discover Lapland',
          },
          {
            property: 'og:locale',
            content: 'fi_FI',
          },
          {
            property: 'fb:app_id',
            content: 1570878686357324,
          },
        ]
          .concat(
            keywords.length > 0
              ? {
                  name: `keywords`,
                  content: keywords.join(`, `),
                }
              : []
          )
          .concat(meta)}
      />
      <CookieConsent
        location="bottom"
        buttonText="Hyväksy"
        declineButtonText="Hylkää"
        enableDeclineButton
        cookieName="cookieConsent"
        onAccept={handleAccept}
        style={{
          background: '#2B373B',
          fontSize: '14px',
        }}
        buttonStyle={{
          background: '#4CAF50',
          color: '#fff',
          fontSize: '14px',
          borderRadius: '4px',
          padding: '8px 20px',
        }}
        declineButtonStyle={{
          background: 'transparent',
          border: '1px solid #fff',
          color: '#fff',
          fontSize: '14px',
          borderRadius: '4px',
          padding: '8px 20px',
        }}
      >
        Käytämme evästeitä parantaaksemme käyttökokemustasi ja analysoidaksemme
        sivuston liikennettä.{' '}
        <a
          href="/rekisteri-ja-tietosuojaseloste"
          style={{ color: '#4CAF50', textDecoration: 'underline' }}
        >
          Lue lisää
        </a>
      </CookieConsent>
    </>
  );
};

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
};

SEO.propTypes = {
  // eslint-disable-next-line react/require-default-props
  description: PropTypes.string,
  lang: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
};

export default SEO;
